import React, { useState } from 'react';
import { PDFDocument } from 'pdf-lib';
import { Upload, FileOutput, Trash2 } from 'lucide-react';
import Button from '../../ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/Card';
import Input from '../../ui/Input';

interface PDFFile {
  name: string;
  data: ArrayBuffer;
  pageCount: number;
}

interface PageRange {
  start: number;
  end: number;
}

const PDFSplitter: React.FC = () => {
  const [pdfFile, setPdfFile] = useState<PDFFile | null>(null);
  const [pageRanges, setPageRanges] = useState<PageRange[]>([{ start: 1, end: 1 }]);
  const [isLoading, setIsLoading] = useState(false);

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const file = files[0];
    if (file.type === 'application/pdf') {
      const buffer = await file.arrayBuffer();
      const pdf = await PDFDocument.load(buffer);
      const pageCount = pdf.getPageCount();
      
      setPdfFile({ name: file.name, data: buffer, pageCount });
      setPageRanges([{ start: 1, end: pageCount }]);
    }
  };

  const addPageRange = () => {
    if (!pdfFile) return;
    setPageRanges(prev => [...prev, { start: 1, end: pdfFile.pageCount }]);
  };

  const removePageRange = (index: number) => {
    setPageRanges(prev => prev.filter((_, i) => i !== index));
  };

  const updatePageRange = (index: number, field: 'start' | 'end', value: string) => {
    const numValue = parseInt(value) || 1;
    setPageRanges(prev => prev.map((range, i) => {
      if (i === index) {
        return { ...range, [field]: Math.min(Math.max(1, numValue), pdfFile?.pageCount || 1) };
      }
      return range;
    }));
  };

  const splitPDF = async () => {
    if (!pdfFile) return;
    setIsLoading(true);

    try {
      const sourcePdf = await PDFDocument.load(pdfFile.data);

      for (let i = 0; i < pageRanges.length; i++) {
        const range = pageRanges[i];
        const newPdf = await PDFDocument.create();
        
        // Convert to 0-based index
        const startIndex = range.start - 1;
        const endIndex = range.end - 1;
        
        // Copy pages within the range
        for (let pageIndex = startIndex; pageIndex <= endIndex; pageIndex++) {
          const [copiedPage] = await newPdf.copyPages(sourcePdf, [pageIndex]);
          newPdf.addPage(copiedPage);
        }

        // Save and download the split PDF
        const pdfBytes = await newPdf.save();
        const blob = new Blob([pdfBytes], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        
        const link = document.createElement('a');
        link.href = url;
        link.download = `split_${i + 1}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Error splitting PDF:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Split PDF</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {!pdfFile ? (
            <div className="flex items-center justify-center w-full">
              <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600">
                <div className="flex flex-col items-center justify-center pt-5 pb-6">
                  <Upload className="w-8 h-8 mb-3 text-gray-400" />
                  <p className="mb-2 text-sm text-gray-500 dark:text-gray-400">
                    <span className="font-semibold">Click to upload</span> or drag and drop
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">PDF file only</p>
                </div>
                <input
                  type="file"
                  className="hidden"
                  accept=".pdf"
                  onChange={handleFileChange}
                />
              </label>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{pdfFile.name}</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{pdfFile.pageCount} pages</p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setPdfFile(null)}
                >
                  <Trash2 size={16} />
                </Button>
              </div>

              <div className="space-y-4">
                {pageRanges.map((range, index) => (
                  <div key={index} className="flex items-center space-x-4">
                    <div className="flex-1 grid grid-cols-2 gap-4">
                      <Input
                        type="number"
                        min={1}
                        max={pdfFile.pageCount}
                        value={range.start}
                        onChange={(e) => updatePageRange(index, 'start', e.target.value)}
                        label="Start Page"
                      />
                      <Input
                        type="number"
                        min={1}
                        max={pdfFile.pageCount}
                        value={range.end}
                        onChange={(e) => updatePageRange(index, 'end', e.target.value)}
                        label="End Page"
                      />
                    </div>
                    {pageRanges.length > 1 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removePageRange(index)}
                        className="mt-6"
                      >
                        <Trash2 size={16} />
                      </Button>
                    )}
                  </div>
                ))}
              </div>

              <div className="flex justify-between">
                <Button
                  variant="outline"
                  onClick={addPageRange}
                >
                  Add Range
                </Button>
                <Button
                  onClick={splitPDF}
                  isLoading={isLoading}
                  leftIcon={<FileOutput size={16} />}
                >
                  Split PDF
                </Button>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default PDFSplitter;